import {
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  inject,
  computed,
  effect
} from '@angular/core';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';

// Services
import { CompareLineChartService } from '@portal/app/dashboard/home-page/components/compare-line-chart/compare-line-chart.service';
import { DropdownService } from '@portal/app/dashboard/home-page/services/dropdown.service';
import { FilterService } from '@portal/app/dashboard/home-page/services/filter.service';
import { HomepageService } from '@portal/app/dashboard/home-page/services/home-page.service';
import { FiscalCalendarStateService } from '@portal/app/shared/services/fiscal-calendar-state.service';
import { FiscalDatePresetService } from '@portal/app/shared/services/fiscal-date-preset.service';
import { DayJsDateFormat } from '@portal/app/shared/services/date-time.service';

// Types
import type { Options } from 'highcharts';
import type { FieldDefinitions } from '@portal/app/shared/types';

@Component({
  selector: 'portal-compare-line-chart',
  templateUrl: './compare-line-chart.component.html'
})
export class CompareLineChartComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  compare = 'mediaSpend';
  compare2 = 'roasI';
  fields: FieldDefinitions = {};
  isDataAvailable = true;
  isLoading = true;
  metricOptions: SelectItemGroup[] = [];
  multiLineChartOptions: Options = {};
  private destroy$ = new Subject<void>();
  relativeDay = 'Latest 30 Days';
  relativeDayOptions: SelectItem[] = [];
  viewInitialized = false;

  private fiscalCalendarStateService = inject(FiscalCalendarStateService);
  private fiscalDatePresetService = inject(FiscalDatePresetService);

  fiscalRelativeDayOptions = computed<SelectItem[]>(() => {
    return this.fiscalDatePresetService.datePresetOptions.map((option) => ({
      label: option.label,
      value: option.value
    }));
  });

  isFiscalCalendarEnabled = computed(() =>
    this.fiscalCalendarStateService.isCustomCalendarEnabled()
  );

  constructor(
    private compareLineChartService: CompareLineChartService,
    private dropdownService: DropdownService,
    private filterService: FilterService,
    private homepageService: HomepageService
  ) {
    effect(() => {
      const isFiscalEnabled = this.isFiscalCalendarEnabled();
      this.relativeDayOptions = isFiscalEnabled
        ? this.fiscalRelativeDayOptions()
        : this.dropdownService.getRelativeDayOptions();

      const currentRD = this.compareLineChartService.getRelativeDay();
      if (currentRD) {
        Promise.resolve().then(() => {
          this.relativeDay = currentRD;
        });
      }
    });
  }

  get isChartReady(): boolean {
    return (
      this.viewInitialized &&
      !this.isLoading &&
      !!this.multiLineChartOptions?.series &&
      !!this.multiLineChartOptions?.xAxis &&
      !!this.multiLineChartOptions?.yAxis &&
      !!this.multiLineChartOptions?.plotOptions &&
      !!this.multiLineChartOptions?.tooltip
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.viewInitialized = true;
    });
  }

  ngOnInit(): void {
    this.subscribeToData();

    this.initializeRelativeDayOptions();

    this.metricOptions = this.dropdownService.getGroupedMetricOptions(true);
  }

  private initializeRelativeDayOptions(): void {
    this.relativeDayOptions = this.isFiscalCalendarEnabled()
      ? this.fiscalRelativeDayOptions()
      : this.dropdownService.getRelativeDayOptions();
  }

  subscribeToData() {
    this.compareLineChartService.currentFilters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const relativeDay = this.compareLineChartService.getRelativeDay();
        if (relativeDay) {
          this.relativeDay = relativeDay;
        }
      });

    this.compareLineChartService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });

    this.compareLineChartService.chartData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((chartData) => {
        if (chartData) {
          this.multiLineChartOptions = chartData;
          this.isDataAvailable =
            this.homepageService.checkChartDataAvailability(chartData);
        }
      });

    this.homepageService.compareChartPrimaryMetric$
      .pipe(takeUntil(this.destroy$))
      .subscribe((compareChartPrimaryMetric) => {
        if (compareChartPrimaryMetric) {
          this.compare = compareChartPrimaryMetric;
        }
      });

    this.homepageService.compareChartSecondaryMetric$
      .pipe(takeUntil(this.destroy$))
      .subscribe((compareChartSecondaryMetric) => {
        if (compareChartSecondaryMetric) {
          this.compare2 = compareChartSecondaryMetric;
        }
      });
  }

  getCompareOptions(): SelectItem[] {
    const fieldValues = Object.values(this.fields);

    return fieldValues.map((option) => ({
      label: option.label,
      value: option.literalId
    }));
  }

  onCompareChange(newValue: string) {
    this.compare = newValue;
    this.compareLineChartService.setChartDimensions([
      this.compare,
      this.compare2
    ]);
    this.saveLayout();
  }

  onCompare2Change(newValue: string) {
    this.compare2 = newValue;
    this.compareLineChartService.setChartDimensions([
      this.compare,
      this.compare2
    ]);
    this.saveLayout();
  }

  onRelativeDayChange(newValue: string) {
    this.relativeDay = newValue;

    let dateFilters;

    if (this.fiscalCalendarStateService.isCustomCalendarEnabled()) {
      const yesterday = dayjs().subtract(1, 'day');
      const fiscalDateRange =
        this.fiscalDatePresetService.calculateDateRange(newValue, yesterday);
      if (fiscalDateRange) {
        const startDate = fiscalDateRange.startDate.format(
          DayJsDateFormat.fullDate
        );
        const endDate = fiscalDateRange.endDate.format(
          DayJsDateFormat.fullDate
        );

        const compareDateRange =
          this.fiscalDatePresetService.calculateCompareDateRange(
            'previous_period',
            fiscalDateRange
          );

        dateFilters = this.filterService.getDateFiltersFromDates(
          startDate,
          endDate,
          compareDateRange?.startDate.format(DayJsDateFormat.fullDate),
          compareDateRange?.endDate.format(DayJsDateFormat.fullDate),
          newValue
        );
      }
    } else {
      dateFilters = this.filterService.getDateFilters(newValue);
    }

    if (dateFilters) {
      this.compareLineChartService.fetchAndSetChartDataWithTempFilters(
        dateFilters
      );
    }
  }

  saveLayout() {
    const layoutData = {
      compareChartPrimaryMetric: this.compare,
      compareChartSecondaryMetric: this.compare2
    };

    this.homepageService.updateHomepageLayout(layoutData).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) => console.error(error)
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
